import { useEffect, useState } from "react";

interface WeatherData {
  temperature: number;
  weatherCode: number;
  humidity: number;
  windSpeed: number;
  isDay: number;
}

const weatherCodes: Record<number, { zh: string; en: string; icon: string }> = {
  0: { zh: "晴朗", en: "Clear", icon: "ri-sun-line" },
  1: { zh: "大部晴朗", en: "Mainly Clear", icon: "ri-sun-cloudy-line" },
  2: { zh: "多云", en: "Partly Cloudy", icon: "ri-cloudy-line" },
  3: { zh: "阴天", en: "Overcast", icon: "ri-cloud-line" },
  45: { zh: "雾", en: "Fog", icon: "ri-foggy-line" },
  48: { zh: "雾凇", en: "Rime Fog", icon: "ri-foggy-line" },
  51: { zh: "毛毛雨", en: "Drizzle", icon: "ri-drizzle-line" },
  53: { zh: "中度毛毛雨", en: "Moderate Drizzle", icon: "ri-drizzle-line" },
  55: { zh: "密集毛毛雨", en: "Dense Drizzle", icon: "ri-drizzle-line" },
  61: { zh: "小雨", en: "Slight Rain", icon: "ri-rainy-line" },
  63: { zh: "中雨", en: "Moderate Rain", icon: "ri-rainy-line" },
  65: { zh: "大雨", en: "Heavy Rain", icon: "ri-heavy-showers-line" },
  71: { zh: "小雪", en: "Slight Snow", icon: "ri-snowy-line" },
  73: { zh: "中雪", en: "Moderate Snow", icon: "ri-snowy-line" },
  75: { zh: "大雪", en: "Heavy Snow", icon: "ri-snowy-line" },
  80: { zh: "阵雨", en: "Rain Showers", icon: "ri-showers-line" },
  81: { zh: "中度阵雨", en: "Moderate Showers", icon: "ri-showers-line" },
  82: { zh: "暴雨", en: "Violent Showers", icon: "ri-heavy-showers-line" },
  95: { zh: "雷雨", en: "Thunderstorm", icon: "ri-thunderstorms-line" },
  96: { zh: "雷雨伴冰雹", en: "Thunderstorm & Hail", icon: "ri-thunderstorms-line" },
  99: { zh: "强雷雨伴冰雹", en: "Heavy Thunderstorm", icon: "ri-thunderstorms-line" },
};

function getWeatherInfo(code: number) {
  return weatherCodes[code] ?? { zh: "未知", en: "Unknown", icon: "ri-question-line" };
}

interface WeatherWidgetProps {
  lang: "zh" | "en";
}

export default function WeatherWidget({ lang }: WeatherWidgetProps) {
  const [weather, setWeather] = useState<WeatherData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    // 北京坐标 (39.9042, 116.4074)
    const lat = 39.9042;
    const lon = 116.4074;

    fetch(
      `https://api.open-meteo.com/v1/forecast?latitude=${lat}&longitude=${lon}&current=temperature_2m,relative_humidity_2m,weather_code,wind_speed_10m,is_day&timezone=auto`
    )
      .then((res) => res.json())
      .then((data) => {
        if (data.current) {
          setWeather({
            temperature: Math.round(data.current.temperature_2m),
            weatherCode: data.current.weather_code,
            humidity: data.current.relative_humidity_2m,
            windSpeed: data.current.wind_speed_10m,
            isDay: data.current.is_day,
          });
        }
        setLoading(false);
      })
      .catch(() => {
        setError(true);
        setLoading(false);
      });
  }, []);

  if (loading) {
    return (
      <div className="rounded-xl bg-gradient-to-br from-[#0c1f1a] via-xh-bg-card to-xh-bg-deep border border-cyan-500/20 p-5">
        <p className="text-gray-500 text-xs mb-2 tracking-wider uppercase">
          {lang === "zh" ? "北京实时天气" : "Beijing Live Weather"}
        </p>
        <div className="flex items-center gap-3 mt-3">
          <i className="ri-loader-4-line text-cyan-400 text-2xl animate-spin" />
          <span className="text-gray-400 text-sm">{lang === "zh" ? "天气获取中..." : "Fetching weather..."}</span>
        </div>
      </div>
    );
  }

  if (error || !weather) {
    return (
      <div className="rounded-xl bg-gradient-to-br from-[#0c1f1a] via-xh-bg-card to-xh-bg-deep border border-cyan-500/20 p-5">
        <p className="text-gray-500 text-xs mb-2 tracking-wider uppercase">
          {lang === "zh" ? "北京实时天气" : "Beijing Live Weather"}
        </p>
        <div className="flex items-center gap-3 mt-3">
          <i className="ri-error-warning-line text-gray-500 text-2xl" />
          <span className="text-gray-500 text-sm">{lang === "zh" ? "暂时无法获取" : "Unavailable"}</span>
        </div>
      </div>
    );
  }

  const info = getWeatherInfo(weather.weatherCode);

  return (
    <div className="relative overflow-hidden rounded-xl bg-gradient-to-br from-[#0c1f1a] via-xh-bg-card to-xh-bg-deep border border-cyan-500/20 p-5 hover:border-cyan-500/40 transition-all duration-300">
      {/* Decorative glow */}
      <div className="absolute top-0 right-0 w-32 h-32 bg-cyan-500/5 rounded-full -translate-y-1/2 translate-x-1/2 pointer-events-none" />

      <div className="flex items-center justify-between">
        <div>
          <p className="text-gray-500 text-xs mb-2 tracking-wider uppercase">
            {lang === "zh" ? "北京实时天气" : "Beijing Live Weather"}
          </p>
          <div className="flex items-baseline gap-3">
            <span className="text-white text-4xl font-bold tracking-tight">{weather.temperature}°</span>
            <span className="text-cyan-400 text-sm font-medium">{lang === "zh" ? info.zh : info.en}</span>
          </div>
        </div>
        <div className="w-14 h-14 flex items-center justify-center rounded-2xl bg-cyan-500/10 flex-shrink-0">
          <i className={`${info.icon} text-cyan-400 text-3xl`} />
        </div>
      </div>

      <div className="flex items-center gap-5 mt-4 pt-4 border-t border-white/5">
        <span className="text-gray-400 text-xs flex items-center gap-1.5">
          <i className="ri-drop-line text-cyan-400/70" />
          {lang === "zh" ? "湿度" : "Humidity"}&nbsp;{weather.humidity}%
        </span>
        <span className="text-gray-400 text-xs flex items-center gap-1.5">
          <i className="ri-windy-line text-cyan-400/70" />
          {lang === "zh" ? "风速" : "Wind"}&nbsp;{weather.windSpeed} km/h
        </span>
        <span className="text-gray-400 text-xs flex items-center gap-1.5">
          <i className="ri-map-pin-line text-cyan-400/70" />
          {lang === "zh" ? "北京市" : "Beijing, CN"}
        </span>
      </div>
    </div>
  );
}